import React from "react";
import * as Yup from "yup";

import { Grid, Paper } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  getAllClientsNoPagination,
  workflowContainerEmptyById,
  workflowContainerEmptyPost,
  workflowContainerEmptyPut,
} from "../../../../libs/apis";
import {
  Button as ButtonAntd,
  Popconfirm,
  ConfigProvider,
  Segmented,
  Space,
  message,
  // Progress,
} from "antd";
import { CheckCircleFilled, ClearOutlined } from "@ant-design/icons";
import { useWorkflowStore } from "../../store";
import { ClientI } from "../../../../libs/interfaces";
import { ThirdComponent } from "./third-component/page";
import { SecondComponent } from "./second-component/page";
import { FirstComponent } from "./first-component";
import { useLocalStorage, useWidth } from "../../../../libs/hooks";
import imageBg from "../../../../images/bg-equipments.jpg";
import { NAVBAR_HEIGHT_SUM } from "../../../../libs/constants";
import { css } from "@emotion/css";
import { useShallow } from "zustand/react/shallow";
import { useRouterStore } from "../../../../libs/stores";
import Swal from "sweetalert2";
import { obtenerFotoConWatermarkBase64 } from "../../../../libs/hooks";
import { useSnackbar } from "@components";

const WorkflowAddComponent = () => {
  const width = useWidth();

  return (
    <Grid
      container
      spacing={0.5}
      justifyContent="center"
      sx={{
        mt: 0,
        maxHeight: `calc(100vh - ${NAVBAR_HEIGHT_SUM})`,
        minHeight: `calc(100vh - ${NAVBAR_HEIGHT_SUM})`,
        height: `calc(100vh - ${NAVBAR_HEIGHT_SUM})`,
        overflowY: "hidden",
        overflowX: "hidden",
        backgroundImage: `url(${imageBg})`,
      }}
    >
      <Grid item xs={12} md={6}>
        <Paper elevation={2} sx={{ m: width === "xs" ? 0 : 1 }}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Component width={width} />
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

interface ComponentPropsI {
  width: string;
}

const Component = ({ width }: ComponentPropsI) => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const setSection = useRouterStore((state) => state.setSection);

  const [clients, setClients] = React.useState<ClientI[]>([]);
  const step = useWorkflowStore((state) => state.step);

  const setDataContainerEmpty = useWorkflowStore(
    (state) => state.setDataContainerEmpty
  );

  const onClear = useWorkflowStore(useShallow((state) => state.onClear));
  const [activeKey, setActiveKey] = useLocalStorage<string[]>(
    "active-key-empty",
    ["1"]
  );

  React.useEffect(() => {
    handleGetData();
    setSection("Vacío 🚚");
    return () => {
      setSection("");
      if (id) {
        onClear();
        setActiveKey(["1"]);
      }
    };
  }, []);

  const handleGetData = async () => {
    try {
      if (id) {
        const { data: dataWf } = await workflowContainerEmptyById(Number(id));
        if (!dataWf) return;

        setDataContainerEmpty(dataWf);
      }

      const { data: dataClients } = await getAllClientsNoPagination();
      if (!dataClients) return;
      setClients(dataClients);
    } catch (err: any) {
      message.error(err.toString());
    }
  };

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
        overflowY: "hidden",
        overflowX: "hidden",
      }}
    >
      <HeaderComponent
        width={width}
        id={id}
        setActiveKey={setActiveKey}
        enqueueSnackbar={enqueueSnackbar}
      />
      {step === "Formulario" ? (
        <FirstComponent
          clients={clients}
          width={width}
          enqueueSnackbar={enqueueSnackbar}
        />
      ) : step === "Códigos" ? (
        <SecondComponent widthFull={width} />
      ) : step === "Fotos" ? (
        <ThirdComponent
          widthFull={width}
          activeKey={activeKey}
          setActiveKey={setActiveKey}
        />
      ) : (
        <FirstComponent
          clients={clients}
          width={width}
          enqueueSnackbar={enqueueSnackbar}
        />
      )}
    </div>
  );
};

interface HeaderPropsI {
  width: string;
  id: string | undefined;
  setActiveKey: (key: any) => void;
  enqueueSnackbar: (message: string, options?: any) => void;
}

const HeaderComponent = ({
  width,
  id,
  setActiveKey,
  enqueueSnackbar,
}: HeaderPropsI) => {
  const navigate = useNavigate();
  const { getPrefixCls } = React.useContext(ConfigProvider.ConfigContext);
  const rootPrefixCls = getPrefixCls();
  const [isProcessing, setProcessing] = React.useState(false);
  const firstTakePhoto = useWorkflowStore(
    (state) => state.firstTakePhoto
  );
  const onClear = useWorkflowStore((state) => state.onClear);
  const resetSteps = useWorkflowStore((state) => state.resetSteps);
  const getOnlyPhotos = useWorkflowStore((state) => state.getOnlyPhotos);

  const validationSchema = Yup.object({
    container: Yup.string().required("N° Contenedor Requerido"),
    clientId: Yup.string().required("Exportador Requerido"),
    producerId: Yup.string().required("Productor Requerido"),
    farmId: Yup.string().required("Hacienda Requerida"),
    magap: Yup.string().required("Magap Requerido"),
    boxId: Yup.string().required("Tipo de Caja Requerido"),
    brandId: Yup.string().required("Marca Requerido"),

    startProcess: Yup.string().required("Hora Inicio de Proceso Requerido"),
    containerArrival: Yup.string().required(
      "Hora Arribo de Contenedor Requerido"
    ),
    containerOpen: Yup.string().required(
      "Hora Apertura de Contenedor Requerido"
    ),
    startLoading: Yup.string().required(
      "Hora Inicio de Carga de Contenedor Requerido"
    ),

    numKit: Yup.string().required("Número de Kit Vacío"),
    siglasEPhoto: Yup.string().required("Foto N° Contenedor Vacio requerido"),
    eir: Yup.string().required("El Eir es requerido"),
    eirPhoto: Yup.string().required("Foto Eir requerido"),
    ciConductor: Yup.string().required("CI Conductor requerido"),
    ciConductorPhoto: Yup.string().required("Foto CI Conductor requerido"),
    placa: Yup.string().required("Placa requerido"),
    placaPhoto: Yup.string().required("Foto Placa requerido"),
    cabezalPhoto: Yup.string().required("Foto Cabezal requerido"),
    etiquetaVerificadoraPuerta: Yup.string().required(
      "Etiqueta Verificadora Puerta requerido"
    ),
    etiquetaVerificadoraPuertaPhoto: Yup.string().required(
      "Foto etiqueta Verificadora Puerta requerido"
    ),
    // etiquetasVentoleras: Yup.string().required(
    //   "Etiquetas Ventoleras requerido"
    // ),
    etiquetasVentolerasPhoto1: Yup.string().required(
      "Foto 1 Etiquetas Ventoleras requerido"
    ),
    etiquetasVentolerasPhoto2: Yup.string().required(
      "Foto 2 Etiquetas Ventoleras requerido"
    ),
    // etiquetasPanelExterior: Yup.string().required(
    //   "Etiquetas Panel Exterior requerido"
    // ),
    etiquetasPanelExteriorPhoto1: Yup.string().required(
      "Etiquetas Panel Exterior Foto1 requerido"
    ),
    etiquetasPanelExteriorPhoto2: Yup.string().required(
      "Etiquetas Panel Exterior Foto2 requerido"
    ),
    // etiquetasPanelInterior: Yup.string().required(
    //   "Etiquetas Panel Interior requerido"
    // ),
    etiquetasPanelInteriorPhoto1: Yup.string().required(
      "Etiquetas Panel Interior Foto1 requerido"
    ),
    etiquetasPanelInteriorPhoto2: Yup.string().required(
      "Etiquetas Panel Interior Foto2 requerido"
    ),
    etiquetasPanelInteriorPhoto3: Yup.string().required(
      "Etiquetas Panel Interior Foto3 requerido"
    ),
    plastico: Yup.string().required("Plastico requerido"),
    plasticoPhoto: Yup.string().required("plastico Foto requerido"),
    botellaRastreo: Yup.string().required("Botella Rastreo requerido"),
    botellaRastreoPhoto: Yup.string().required(
      "Botella Rastreo Foto requerido"
    ),
    numRastreoSatelital: Yup.string().required(
      "Num Rastreo Satelital requerido"
    ),
    numRastreoSatelitalPhoto: Yup.string().required(
      "Num Rastreo Satelital Foto requerido"
    ),
    etiquetaExporPuerta: Yup.string().required(
      "Etiqueta Exportador Puerta requerido"
    ),
    etiquetaExporPuertaPhoto: Yup.string().required(
      "Etiqueta Exportador Puerta Foto requerido"
    ),
    macroEtiquetasVentoleras: Yup.string().required(
      "Macro Etiquetas Ventoleras requerido"
    ),
    macroEtiquetasVentolerasPhoto1: Yup.string().required(
      "Macro Etiquetas Ventoleras Foto1 requerido"
    ),
    macroEtiquetasVentolerasPhoto2: Yup.string().required(
      "Macro Etiquetas Ventoleras Foto2 requerido"
    ),
    paredExternaLadoDerechoPhoto: Yup.string().required(
      "Pared Externa Lado Derecho Foto requerido"
    ),
    paredExternaLadoIzquierdoPhoto: Yup.string().required(
      "Pared Externa Lado Izquierdo Foto requerido"
    ),
    panelExteriorPhoto: Yup.string().required("Panel Exterior Foto requerido"),
    // techoExteriorPhoto: Yup.string().required('Techo Exterior Foto requerido'),
    pisoIzquierdoExteriorPhoto: Yup.string().required(
      "Piso Izquierdo Exterior Foto requerido"
    ),
    pisoDerechoExteriorPhoto: Yup.string().required(
      "Piso Derecho Exterior Foto requerido"
    ),
    paredInternaLadoDerechoPhoto: Yup.string().required(
      "Pared Interna Lado Derecho Foto requerido"
    ),
    paredInternaLadoIzquierdoPhoto: Yup.string().required(
      "Pared Interna Lado Izquierdo Foto requerido"
    ),
    panelInternoPhoto1: Yup.string().required("Panel Interno Foto requerido"),
    techoInternoPhoto: Yup.string().required("Techo Interno Foto requerido"),
    pisoInternoPhoto: Yup.string().required("Piso Interno Foto requerido"),
    macroEtiquetasVentolerasAdicionalPhoto1: Yup.string().required(
      "Pack Sellos Foto Izquierda requerido"
    ),
    macroEtiquetasVentolerasAdicionalPhoto2: Yup.string().required(
      "Pack Sellos Foto Derecha requerido"
    ),
    // parchesPhoto1: Yup.string().required('Parches Foto1 requerido'),
    // parchesPhoto2: Yup.string().required('Parches Foto2 requerido'),
    // parchesPhoto3: Yup.string().required('Parches Foto3 requerido'),
    // parchesExtPhoto1: Yup.string().required('Parches Exterior Foto1 requerido'),
    // parchesExtPhoto2: Yup.string().required('Parches Exterior Foto2 requerido'),
    // parchesExtPhoto3: Yup.string().required('Parches Exterior Foto3 requerido'),

    // macroEtiquetasVentolerasAdicional: Yup.string().required('// macroEtiquetasVentolerasAdicional requerido'),
    // macroEtiquetasVentolerasAdicionalId: Yup.string().required('// macroEtiquetasVentolerasAdicionalId requerido'),
    // macroEtiquetasVentolerasAdicionalPhoto1: Yup.string().required('// macroEtiquetasVentolerasAdicional Foto1 requerido'),
    // macroEtiquetasVentolerasAdicionalPhoto2: Yup.string().required('// macroEtiquetasVentolerasAdicional Foto2 requerido'),
  });

  const getDataContainerEmpty = useWorkflowStore(
    (state) => state.getDataContainerEmpty
  );

  const linearGradientButton = css`
    &.${rootPrefixCls}-btn-primary:not([disabled]):not(
        .${rootPrefixCls}-btn-dangerous
      ) {
      border-width: 0;

      > span {
        position: relative;
      }

      &::before {
        content: "";
        background: linear-gradient(135deg, #227317ff, #92b910ff);
        position: absolute;
        inset: 0;
        opacity: 1;
        transition: all 0.3s;
        border-radius: inherit;
      }

      &:hover::before {
        opacity: 0;
      }
    }
  `;

  async function buildFinalData() {
    const dateHelper = new Date();
    const data = { ...getOnlyPhotos() };
    const dataTotal = { ...getDataContainerEmpty() };
    dataTotal.timeStampSave = firstTakePhoto ?? dateHelper.toISOString();
    dataTotal.hourSaveUser = dateHelper.toLocaleTimeString();

    for (const [clave, valor] of Object.entries(data)) {
      if (
        !valor ||
        valor.startsWith("http://") ||
        valor.startsWith("https://") ||
        valor.startsWith("data:")
      )
        continue;
      data[clave] = await obtenerFotoConWatermarkBase64(valor as string);
    }

    return { ...dataTotal, ...data };
  }

  const handleSave = async () => {
    try {
      setProcessing(true);
      message.loading("Procesando las fotos", 0);
      const objPost = await buildFinalData();
      await validationSchema.validate(objPost);
      message.destroy();
      message.loading("Guardando Información", 0);

      if (id) {
        const { data: dataPut } = await workflowContainerEmptyPut(
          Number(id),
          objPost
        );
        if (!dataPut) return;
        message.destroy();
        message.success(dataPut);
      } else {
        const { data: dataPost } = await workflowContainerEmptyPost(objPost);
        if (!dataPost) return;
        message.destroy();
        Swal.fire({
          title: "¡Registro exitoso!",
          icon: "success",
          text: dataPost.alert,
          confirmButtonText: "Aceptar",
        });
      }
      resetSteps();
      // onClear();
      navigate("/container/empty/list");
      setActiveKey(["1"]);
      setProcessing(false);
    } catch (err: any) {
      message.destroy();
      setProcessing(false);
      enqueueSnackbar(err.toString(), { variant: "error" });
    }
  };

  const handleResetWithFear = () => {
    Swal.fire({
      title: "¡Atención!",
      html: `<p style="font-weight:bolder; color:red">Estás a punto de BORRAR todo. <br/> ¿Estás seguro?</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Sí, eliminar todo",
      cancelButtonText: "No, cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        onClear();
        resetSteps();
        Swal.fire({
          title: "¡Listo!",
          text: "Toda la información fue borrada",
          icon: "success",
          confirmButtonColor: "#271080",
        });
      }
    });
  };

  return (
    <div
      style={{
        position: "absolute",
        top: width === "xs" ? -7 : -8,
        width: "100%",
        height: 50,
        background: "white",
        zIndex: 701,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        alignContent: "center",
        padding: 0.5,
        borderBottom: "1px dashed gainsboro",
      }}
    >
      <StepComponent />
      <div>
        <ConfigProvider
          button={{
            className: linearGradientButton,
          }}
        >
          <Space size="small">
            <Popconfirm
              title="Se borrará toda la Información"
              description="Desea Continuar?"
              onConfirm={handleResetWithFear}
              zIndex={1601}
              okText="Si"
              cancelText="No"
              okButtonProps={{ size: "middle", style: { fontWeight: "bold" } }}
              cancelButtonProps={{ size: "middle" }}
            >
              <ButtonAntd
                size="middle"
                type="primary"
                danger
                icon={<ClearOutlined />}
              />
            </Popconfirm>
            <Popconfirm
              title="Finalizar Proceso"
              description="Si Finaliza no podrá editar la Información"
              onConfirm={handleSave}
              okText="Si"
              cancelText="No"
              okButtonProps={{ size: "middle", style: { fontWeight: "bold" } }}
              cancelButtonProps={{ size: "middle" }}
              icon={<CheckCircleFilled />}
              zIndex={1601}
            >
              <ButtonAntd
                size="middle"
                loading={isProcessing}
                type="primary"
                icon={<CheckCircleFilled />}
                disabled={isProcessing}
              >
                Finalizar
              </ButtonAntd>
            </Popconfirm>
          </Space>
        </ConfigProvider>
      </div>
    </div>
  );
};

const StepComponent = () => {
  const step = useWorkflowStore((state) => state.step);
  const setStep = useWorkflowStore((state) => state.setStep);

  return (
    <Segmented
      options={["Formulario", "Códigos", "Fotos"]}
      value={step}
      onChange={setStep}
    />
  );
};

export default WorkflowAddComponent;
